import { useContext } from 'react';
import { useAtom } from 'jotai';
import useEvents from 'lib/hooks/useEvents';
import ColorTap from 'lib/Tracking/Srp/SearchFilters/ColorFilter/ColorTap';
import ExpandCollapseTap from 'lib/Tracking/Srp/SearchFilters/ExpandCollapseTap';
import { useColorsFilter } from 'lib/hooks/useSrpFilters';
import { Features } from 'lib/EventSchema';

import Accordion from 'components/Shared/SearchFilter/Accordion';
import SearchFilterSubHeader from 'components/Shared/SearchFilter/SearchFilterSubHeader';
import ColorSelector from 'components/Shared/SearchFilter/ColorSelector';
import { ExteriorColorType, InteriorColorType } from 'types/srpFilters';
import SrpEventContext from 'contexts/SrpEventContext';
import { ExteriorColorFacet, InteriorColorFacet } from 'lib/apis/SearchApi/types';
import FilterAccordionAtom, { FilterAccordionName } from 'atoms/FilterAccordionAtom';
import styles from './styles';

const interiorColors = ['White', 'Gray', 'Black', 'Brown', 'Red', 'Blue'];
const exteriorColors = [
  'White',
  'Gray',
  'Black',
  'Yellow',
  'Orange',
  'Brown',
  'Red',
  'Green',
  'Blue',
  'Purple',
  'Silver',
  'Gold',
];

export interface ColorsFilterProps {
  expanded?: boolean;
  exteriorColorFacets?: ExteriorColorFacet;
  interiorColorFacets?: InteriorColorFacet;
}

export default function ColorsFilter(props: ColorsFilterProps): JSX.Element {
  const logger = useEvents();
  const { sourcePage } = useContext(SrpEventContext);
  const { value, setValue } = useColorsFilter();

  const [filterAccordionAtom, setFilterAccordionAtom] = useAtom(FilterAccordionAtom);
  const filterExpanded = filterAccordionAtom[FilterAccordionName.Colors];

  function handleAccordionClick(): void {
    setFilterAccordionAtom({
      [FilterAccordionName.Colors]: !filterExpanded,
    });
    logger.log(
      ExpandCollapseTap({ feature: Features.color, expanded: !filterExpanded, sourcePage })
    );
  }

  function onTap(colorType: string, label: string, isSelected: boolean): void {
    setValue({ ...value, [colorType]: { [label.toLowerCase()]: isSelected } });
    logger.log(ColorTap(colorType, label, sourcePage));
  }

  return (
    <Accordion title={'colors'} expanded={filterExpanded} onClick={handleAccordionClick}>
      <SearchFilterSubHeader subTitle={'exterior'} />
      <div className="outer-colors-container exterior-colors">
        <div className="inner-colors-container">
          {exteriorColors.map((exteriorColor) => {
            const lowercaseColor = exteriorColor.toLowerCase() as ExteriorColorType;
            return (
              <div className="color-filter-selector" key={exteriorColor}>
                <ColorSelector
                  color={exteriorColor}
                  active={value.exterior[lowercaseColor]}
                  onClick={(isSelected: boolean): void =>
                    onTap('exterior', exteriorColor, !isSelected)
                  }
                  qualifier="exterior"
                  facetCount={props.exteriorColorFacets?.[lowercaseColor]}
                />
              </div>
            );
          })}
        </div>
      </div>
      <SearchFilterSubHeader subTitle={'interior'} />
      <div className="outer-colors-container interior-colors">
        <div className="inner-colors-container">
          {interiorColors.map((interiorColor) => {
            const lowercaseColor = interiorColor.toLowerCase() as InteriorColorType;
            return (
              <div className="color-filter-selector" key={interiorColor}>
                <ColorSelector
                  color={interiorColor}
                  active={value.interior[lowercaseColor]}
                  onClick={(isSelected: boolean): void =>
                    onTap('interior', interiorColor, !isSelected)
                  }
                  qualifier="interior"
                  facetCount={props.interiorColorFacets?.[lowercaseColor]}
                />
              </div>
            );
          })}
        </div>
      </div>
      <style jsx>{styles}</style>
    </Accordion>
  );
}
